/**
 * useGiftPopup — Controls the gift/reward popup modal state and display logic.
 */
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { RealtimePostgresInsertPayload } from "@supabase/supabase-js";
import { logger } from '@/utils/logger';

interface GiftPopupData {
  pledgeId: string;
  goalTitle?: string;
  reward?: string;
  dueDate?: string;
  senderName?: string;
  message?: string;
}

export function useGiftPopup() {
  const { user } = useAuth();
  const [popupData, setPopupData] = useState<GiftPopupData | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const getShownPledges = (): Set<string> => {
    try {
      const stored = sessionStorage.getItem("shown_gift_pledges");
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  };

  const markPledgeShown = (pledgeId: string) => {
    const shown = getShownPledges();
    shown.add(pledgeId);
    sessionStorage.setItem("shown_gift_pledges", JSON.stringify([...shown]));
  };

  const dismissPopup = useCallback(() => {
    setIsOpen(false);
    setPopupData(null);
  }, []);

  const acceptFromPopup = useCallback(async () => {
    if (!popupData) return false;
    try {
      const { error } = await supabase.rpc("accept_reward_pledge", {
        pledge_id_param: popupData.pledgeId,
      });
      if (error) throw error;
      setIsOpen(false);
      setPopupData(null);
      return true;
    } catch (err) {
      logger.error("[useGiftPopup] Accept error:", err);
      return false;
    }
  }, [popupData]);

  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel("gift-popup")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "reward_pledges",
          filter: `to_user_id=eq.${user.id}`,
        },
        async (payload: RealtimePostgresInsertPayload<Record<string, any>>) => {
          const pledge = payload.new;
          if (pledge.status !== "pending") return;

          const shown = getShownPledges();
          if (shown.has(pledge.id)) return;

          // Parse goal info from custom_reward_details
          let goalTitle: string | undefined;
          let reward: string | undefined;
          let dueDate: string | undefined;
          try {
            const parsed = JSON.parse(pledge.custom_reward_details || "");
            goalTitle = parsed.goal_title;
            reward = parsed.reward;
            dueDate = parsed.due_date;
          } catch {
            reward = pledge.custom_reward_details || "Gift";
          }

          // Get sender name
          let senderName = "Someone";
          const { data: profile } = await supabase
            .from("profiles")
            .select("display_name")
            .eq("id", pledge.from_user_id)
            .single();
          if (profile?.display_name) senderName = profile.display_name;

          markPledgeShown(pledge.id);
          setPopupData({
            pledgeId: pledge.id,
            goalTitle,
            reward,
            dueDate,
            senderName,
            message: pledge.message,
          });
          setIsOpen(true);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  return { isOpen, popupData, dismissPopup, acceptFromPopup };
}
